/* =============================================================================
 * delivery-views.js -- THE THREE VIEWS. Presentation only. No arithmetic.
 *
 * Reads the one data island (snippets/delivery-window.liquid), asks the one
 * decision maker (assets/delivery-clock.js), and renders. Every view is a no-op
 * when its DOM is absent, so this one file serves every page.
 *
 * docs/BRIEF-clock-refactor.md section 1 -- the three surfaces do DIFFERENT jobs, which
 * is why "just unify them" was too blunt and why this is three views over ONE
 * decision rather than one view everywhere:
 *
 *     home     URGENCY        "1 hour 33 minutes left to order for same-day"
 *     product  ELIGIBILITY    can this customer still order this, today?
 *     cart     DELIVERABILITY can we actually deliver on the date they picked?
 *
 * -----------------------------------------------------------------------------
 * THE INVARIANT, restated here because this is where it is easiest to break:
 *
 *     THE GATE FAILS OPEN.  THE DISPLAY FAILS GRACEFUL.
 *
 * decide().ok === false means: allow the order, and show the timeless brand crest
 * (the 'unresolved' state in delivery-countdown.liquid) -- never a fabricated
 * countdown, and never a blank. A false delivery promise would destroy the
 * phone-call recoverability that makes fail-open sound; a blank reads as broken
 * ("scam!") and erodes trust. The crest makes NO time claim, so it is safe in
 * every state. See delivery-clock.js and BRIEF section 6/6a.
 * -----------------------------------------------------------------------------
 *
 * ON CLS: the countdown block is rendered at its FINAL HEIGHT from first paint
 * (the brand crest holds the box), and the resolved text is swapped IN PLACE.
 * There is no reveal, therefore no shift. The old code shipped `.countdown` as `d-none` and
 * removed the class once dayjs had loaded and the logic had run -- +219px at
 * 1468ms, which was 0.2066 of the site's 0.2301 CLS, and the field agreed
 * (RUM cls ~0.209). That reveal is deleted, not reserved-around.
 * ============================================================================= */
(function () {
  'use strict';

  var ISLAND_ID = 'delivery-window-data';
  var lastInput = null;   // kept so a cutoff rollover can re-decide, not hand-roll

  function readIsland() {
    try {
      var el = document.getElementById(ISLAND_ID);
      if (!el) return null;
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;                       // unparseable island -> unresolved -> fail open
    }
  }

  function plural(n, word) { return n + ' ' + word + (n === 1 ? '' : 's'); }

  function setText(sel, txt, root) {
    var nodes = (root || document).querySelectorAll(sel);
    for (var i = 0; i < nodes.length; i++) nodes[i].textContent = txt;
  }

  /* ---------------------------------------------------------------------------
   * VIEW 1 -- HOME: urgency. The countdown ticks; nothing else moves.
   * ------------------------------------------------------------------------- */
  var urgencyTimer = null;

  function renderUrgency(decision) {
    // Re-runnable: the server-clock correction below can call this a second time,
    // and two setIntervals ticking the same nodes would race and double-decrement.
    if (urgencyTimer) { clearInterval(urgencyTimer); urgencyTimer = null; }

    var blocks = document.querySelectorAll('[data-delivery-countdown]');
    if (!blocks.length) return;

    // Two non-active paths below: an UNRESOLVED clock -> the graceful brand crest
    // (fails graceful, never blank), and a RESOLVED-but-closed store -> the closed
    // qualification state. Neither shows a live countdown.
    var i, b;
    if (!decision.ok) {
      // FAIL GRACEFUL: no data we can stand behind -> show the brand crest, never a
      // blank. 'unresolved' renders the timeless "Premier Florist / Since 1871" line
      // at box height (delivery-countdown.liquid); it makes no delivery promise.
      for (i = 0; i < blocks.length; i++) blocks[i].setAttribute('data-state', 'unresolved');
      return;
    }
    if (!decision.sameDayOpen) {
      // CLOSED, but RESOLVED. Keep the box at its height and put the next available
      // delivery day in it. Collapsing here would have inverted the CLS bug onto
      // every Saturday, every Sunday and every weekday evening -- see the note to
      // Jason in snippets/delivery-countdown.liquid.
      /* Two lines, both always populated so the box never changes shape:
       *
       *     Tomorrow          <- relative, computed against the STORE's today
       *     July 21st         <- spelled out, unambiguous in every dialect
       *
       * or, further out:
       *
       *     Tuesday
       *     July 28th
       *
       * This is the qualification surface, not just CLS ballast: someone who needs
       * flowers TODAY finds out in the first second rather than at checkout. */
      var next = decision.nextDeliveryDate;
      var dayLabel = window.DeliveryClock.relativeDayLabel(next, decision.today);
      var dateLabel = window.DeliveryClock.formatMonthDay(next);
      for (i = 0; i < blocks.length; i++) {
        b = blocks[i];
        setText('[data-next-delivery-day]', dayLabel, b);
        setText('[data-next-delivery-date]', dateLabel, b);
        b.setAttribute('data-state', 'closed');
      }
      return;
    }

    var remaining = Math.floor(decision.msRemaining / 1000);

    function paint() {
      if (remaining <= 0) {
        clearInterval(urgencyTimer);
        urgencyTimer = null;
        // The cutoff just passed while they were looking at it. Re-decide rather than
        // hand-rolling the transition -- the clock already knows what comes next.
        renderUrgency(window.DeliveryClock.decide(lastInput));
        return;
      }
      var h = Math.floor(remaining / 3600);
      var m = Math.floor((remaining % 3600) / 60);
      var s = remaining % 60;
      for (var k = 0; k < blocks.length; k++) {
        var b = blocks[k];
        setText('[data-countdown-date]', window.DeliveryClock.formatMonthDay(decision.today), b);
        setText('[data-countdown-hours]', plural(h, 'hour'), b);
        setText('[data-countdown-minutes]', plural(m, 'minute'), b);
        setText('[data-countdown-seconds]', plural(s, 'second'), b);
        b.setAttribute('data-state', 'active');
      }
      remaining--;
    }

    paint();
    urgencyTimer = setInterval(paint, 1000);
  }

  /* DELETED: formatUS() -- MM/DD/YYYY.
   *
   * Every one of the old five sites emitted this, and it is the thing Jason asked
   * us to stop doing: "we have European customers ... I generally try to avoid the
   * compressed date formats only because of the varying ways humans keep dates and
   * spell it out in English instead." 07/08 is 7 August to most of the world and
   * July 8th in the US, and a flower delivery date is the wrong place to discover
   * which one the customer assumed.
   *
   * The replacements are pure, exported from delivery-clock.js, and under test in
   * scripts/delivery-clock.test.js: formatMonthDay(), weekdayName(),
   * relativeDayLabel(). Do not reintroduce a local date formatter here -- an
   * untested formatter in a view file is how you get five of them again. */

  /* ---------------------------------------------------------------------------
   * VIEW 2 -- PRODUCT / anywhere: eligibility. "today" / "tomorrow" / a weekday.
   * ------------------------------------------------------------------------- */
  function renderEligibility(decision) {
    var nameEl = document.getElementById('next-workday-name');
    var dateEl = document.getElementById('next-workday-date');
    if (!nameEl || !dateEl) return;

    // FAIL SILENT: say nothing rather than name a delivery day we cannot stand behind.
    if (!decision.ok) return;

    // Lowercased because this one reads inline in a sentence ("delivered today"),
    // unlike the countdown block where it is a standalone label.
    var next = decision.nextDeliveryDate;
    var label = window.DeliveryClock.relativeDayLabel(next, decision.today);
    nameEl.textContent = (label === 'Today' || label === 'Tomorrow') ? label.toLowerCase() : label;
    dateEl.textContent = window.DeliveryClock.formatMonthDay(next);
  }

  /* ---------------------------------------------------------------------------
   * VIEW 3 -- CART: deliverability. The datepicker, and the validation gate.
   *
   * This is the one that touches the buy path, so it is the one where fail-open
   * is not a nicety. window.isDeliverableDate is the public seam the cart's
   * existing validation calls.
   * ------------------------------------------------------------------------- */
  function wireCart(decision) {
    // The GATE. Exposed unconditionally: when the decision did not resolve,
    // decision.isDeliverable already returns true for everything (fail open), so
    // this seam is safe to call in every state and callers need no null check.
    window.isDeliverableDate = function (ymd) {
      try { return decision.isDeliverable(ymd); }
      catch (e) { return true; }                    // belt and suspenders: still open
    };

    // Kept for the datepicker's datesDisabled option and for scripts/cutoff_probe.js,
    // which asserts the pages agree. Empty when unresolved -- nothing is refusable.
    window.finalBlockoutDates = decision.ok ? (decision._blockouts || []) : [];

    var input = document.getElementById('deliveryTime');
    if (!input || typeof Datepicker === 'undefined') return;

    var picker = new Datepicker(input, {
      autohide: true,
      format: 'yyyy-mm-dd',
      minDate: decision.ok ? decision.nextDeliveryDate : undefined,
      beforeShowDay: function (date) {
        var ymd = date.getFullYear() + '-' +
          String(date.getMonth() + 1).padStart(2, '0') + '-' +
          String(date.getDate()).padStart(2, '0');
        // One rule, one place. The picker asks the same function the validation does,
        // so the calendar can never offer a date the gate would then reject.
        return window.isDeliverableDate(ymd) ? undefined : { enabled: false };
      }
    });
    window.deliveryDatePicker = picker;
  }

  /* ------------------------------------------------------------------------- */
  function boot() {
    var DC = window.DeliveryClock;
    // No clock module = unresolved = fail open. Do not block the buy path on a
    // script that did not load. TELEMETRY AND WIDGETS NEVER OUTRANK THE BUY PATH.
    if (!DC) {
      window.isDeliverableDate = function () { return true; };
      return;
    }

    var island = readIsland() || {};

    function inputAt(nowMs) {
      return {
        nowMs: nowMs,
        tz: island.tz,
        cutoff: island.cutoff,
        cutoffOverride: island.cutoffOverride,
        blockouts: island.blockouts,
        weekendEnables: island.weekendEnables,
        exceptions: island.exceptions,
        exceptionsActive: island.exceptionsActive
      };
    }

    // DECIDE NOW, on the device clock. Correct for effectively every real visitor,
    // costs nothing, and means the countdown paints in the first frame -- which is
    // half of why the CLS goes away.
    var clock = DC.resolveNow({});
    lastInput = inputAt(clock.nowMs);
    var decision = DC.decide(lastInput);

    // Keep the resolved blockout set on the decision for the cart's datepicker and
    // for the probe. Presentation detail, not a second computation.
    decision._blockouts = Array.isArray(island.blockouts) ? island.blockouts : [];

    // Observability, not decoration: scripts/cutoff_probe.js reads this to assert
    // every page reached the SAME decision. A gate that has to guess cries wolf.
    window.deliveryDecision = {
      ok: decision.ok, reason: decision.reason, today: decision.today,
      sameDayOpen: decision.sameDayOpen, msRemaining: decision.msRemaining,
      cutoffWall: decision.cutoffWall, nextDeliveryDate: decision.nextDeliveryDate,
      clockSource: clock.source
    };

    renderUrgency(decision);
    renderEligibility(decision);
    wireCart(decision);

    /* BELT AND SUSPENDERS (requirement 3): ask the server what time it actually is,
     * off the critical path, and only redo the work if the device clock is wrong by
     * enough to change an answer. Measured s7: the Date header is live per-request
     * and within ~1s of NTP (see delivery-clock.js fetchServerNow).
     *
     * The 5-minute threshold is deliberate. A few seconds of drift cannot flip any
     * decision that matters, and re-rendering on it would restart the countdown
     * visibly for no gain. Five minutes is small enough to catch a clock that would
     * mis-answer the cutoff and large enough to ignore ordinary drift. */
    DC.fetchServerNow('/cart.js').then(function (serverMs) {
      if (!serverMs) return;                       // no answer -> keep the device answer
      var skew = Math.abs(serverMs - clock.nowMs);
      window.deliveryDecision.clockSkewMs = serverMs - clock.nowMs;
      if (skew < 5 * 60 * 1000) return;

      lastInput = inputAt(serverMs);
      var corrected = DC.decide(lastInput);
      corrected._blockouts = decision._blockouts;
      window.deliveryDecision.clockSource = 'server-corrected';
      window.deliveryDecision.sameDayOpen = corrected.sameDayOpen;
      window.deliveryDecision.today = corrected.today;
      window.deliveryDecision.nextDeliveryDate = corrected.nextDeliveryDate;
      renderUrgency(corrected);
      renderEligibility(corrected);
      wireCart(corrected);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
